import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Row,
  Spinner,
} from 'reactstrap';

// Import Breadcrumb
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { getAppUserByIdApi, setUserStatusApi, deleteAppUserApi } from 'api/user';
import DeleteModal from '../../components/Common/DeleteModal';
import AccountActions from '../../components/Common/AccountActions';
import { ACCOUNT_ACTIONS } from '../../utils/accountActions';
import notify from '../../utils/notify';
import { useRole } from 'hooks/useRole';

const UserDetails = () => {
  // Get user ID from URL parameters
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isSuperAdmin } = useRole();
  const canManage = isAdmin || isSuperAdmin;
  const [statusModal, setStatusModal] = useState(null); // 'deactivate' | 'reinstate' | 'delete'
  const [actionLoading, setActionLoading] = useState(false);
  
  // State for user data and loading/error status
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await getAppUserByIdApi(id);
      setUserData(response);
      setError(null);
    } catch (err) {
      setError('Failed to fetch user details');
      console.error('Error fetching user:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch user data when component mounts
  useEffect(() => {
    if (id) {
      fetchUserData();
    }
  }, [id]);

  const fullName = userData
    ? [userData.firstName, userData.middleName, userData.lastName].filter(Boolean).join(' ')
    : '';

  const handleStatusChange = async (reason) => {
    const deactivating = statusModal === 'deactivate';
    setActionLoading(true);
    try {
      const result = await setUserStatusApi(id, { status: deactivating ? 'Inactive' : 'Active', reason });
      notify.success(deactivating ? 'User deactivated' : 'User reinstated', (result && result.message));
      await fetchUserData();
    } catch (err) {
      notify.error('Update failed', err.message || 'Failed to update user status.');
    } finally {
      setStatusModal(null);
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await deleteAppUserApi(id);
      notify.success('User deleted', 'The user account was deleted.');
      navigate('/users');
    } catch (err) {
      notify.error('Delete failed', err.message || 'Failed to delete user.');
      setStatusModal(null);
    } finally {
      setActionLoading(false);
    }
  };

  // Meta title
  document.title = 'User Details | SUN Welfare Administration System';

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumb */}
          <Breadcrumbs title="Users" breadcrumbItem="User Details" />
          
          <Row className="mb-3">
            <Col>
              <Link to="/users">
                <Button type="button" color="primary" className="btn">
                  <i className="bx bx-arrow-back"></i> Back
                </Button>
              </Link>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center">
              <Spinner color="primary" />
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          ) : userData ? (
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <CardTitle className="mb-4">User Details</CardTitle>
                    
                    <Row>
                      <Col md={6}>
                        <h5 className="mb-3">Personal Information</h5>
                        <div className="mb-2">
                          <strong>Name:</strong> {userData.firstName}{' '}
                          {userData.middleName} {userData.lastName}
                        </div>
                        <div className="mb-2">
                          <strong>Date of Birth:</strong> {userData.dob}
                        </div>
                        <div className="mb-2">
                          <strong>Email:</strong> {userData.email}
                        </div>
                        <div className="mb-2">
                          <strong>Mobile:</strong> {userData.mobileNumber}
                        </div>
                        <div className="mb-2">
                          <strong>Gender:</strong> {userData.gender}
                        </div>
                        <div className="mb-2">
                          <strong>Role:</strong> {userData.role}
                        </div>
                        <div className="mb-2">
                          <strong>Status:</strong>{' '}
                          <span
                            className={`badge bg-${
                              userData.status === 'Active' ? 'success' : 'danger'
                            }`}
                          >
                            {userData.status}
                          </span>
                        </div>
                      </Col>
                      
                      <Col md={6}>
                        <h5 className="mb-3">Address Information</h5>
                        {userData.addresses && userData.addresses.length > 0 ? (
                          userData.addresses.map((address, index) => (
                            <div
                              key={index}
                              className="mb-3 border p-3 rounded"
                            >
                              <div className="mb-2">
                                <strong>Ward:</strong> {address.wardName}
                              </div>
                              <div className="mb-2">
                                <strong>Constituency:</strong>{' '}
                                {address.constituencyName}
                              </div>
                              <div className="mb-2">
                                <strong>County:</strong> {address.countyName}
                              </div>
                              <div className="mb-2">
                                <strong>Region:</strong> {address.regionName}
                              </div>
                            </div>
                          ))
                        ) : (
                          <p>No address information available</p>
                        )}
                      </Col>
                    </Row>

                    <AccountActions
                      copy={ACCOUNT_ACTIONS.staff}
                      isActive={userData.status !== 'Inactive'}
                      canToggle={canManage}
                      canDelete={canManage}
                      onDeactivate={() => setStatusModal('deactivate')}
                      onReinstate={() => setStatusModal('reinstate')}
                      onDelete={() => setStatusModal('delete')}
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          ) : (
            <div className="alert alert-warning" role="alert">
              No user data found
            </div>
          )}
        </Container>
      </div>

      <DeleteModal
        show={statusModal === 'deactivate'}
        variant="warning"
        title={ACCOUNT_ACTIONS.staff.deactivate.title}
        text={ACCOUNT_ACTIONS.staff.deactivate.message(fullName)}
        effects={ACCOUNT_ACTIONS.staff.deactivate.effects}
        confirmText={ACCOUNT_ACTIONS.staff.deactivate.confirmText}
        requireReason
        loading={actionLoading}
        onDeleteClick={handleStatusChange}
        onCloseClick={() => setStatusModal(null)}
      />
      <DeleteModal
        show={statusModal === 'reinstate'}
        variant="success"
        title={ACCOUNT_ACTIONS.staff.reinstate.title}
        text={ACCOUNT_ACTIONS.staff.reinstate.message(fullName)}
        effects={ACCOUNT_ACTIONS.staff.reinstate.effects}
        confirmText={ACCOUNT_ACTIONS.staff.reinstate.confirmText}
        requireReason
        loading={actionLoading}
        onDeleteClick={handleStatusChange}
        onCloseClick={() => setStatusModal(null)}
      />
      <DeleteModal
        show={statusModal === 'delete'}
        title={ACCOUNT_ACTIONS.staff.remove.title}
        text={ACCOUNT_ACTIONS.staff.remove.message(fullName)}
        effects={ACCOUNT_ACTIONS.staff.remove.effects}
        confirmText={ACCOUNT_ACTIONS.staff.remove.confirmText}
        requireTyping
        loading={actionLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setStatusModal(null)}
      />
    </React.Fragment>
  );
};

export default UserDetails;